/**
 * A flight search started before the customer presses Search.
 *
 * Once a search form holds a complete search — both airports, the dates,
 * passengers and cabin — the page calls prefetchFlightSearch() and the
 * search streams in the background: nothing on screen, nothing logged. When
 * the customer presses Search for that same search, the results page takes
 * it over with takePrefetchedFlightSearch(): whatever has already arrived
 * shows at once and the rest keeps streaming in.
 *
 * Anything different by then — other dates, another passenger, a new cabin
 * — and the early search is dropped: its request is aborted (the server
 * stops working on it too) and the press runs a fresh search, which never
 * waits on the old one. An early search that failed, came back partial or
 * is older than MAX_AGE_MS is never reused either; the press searches again.
 *
 * One early search at a time, held here rather than in a page so it
 * survives the move from the home page to the results page.
 */
import { streamGoogleFlights } from '@/api/functions';
import { newerBuildIsLive } from '@/lib/buildVersion';
import { readPassengerParams, writePassengerParams } from '@/utils/passengerTypes';

/** How long a completed early search may stand in for a fresh one. */
export const PREFETCH_MAX_AGE_MS = 5 * 60 * 1000;
/** Quiet time after the last form change before an early search starts. */
export const PREFETCH_DEBOUNCE_MS = 500;
/** The results page rejects departures sooner than this (handleSearch). */
const MIN_LEAD_DAYS = 21;

const IATA = /^[A-Z]{3}$/;

const ymd = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const validDate = (d) => d instanceof Date && !Number.isNaN(d.getTime());
const airportCode = (value) => String((value && typeof value === 'object' ? value.code : value) || '').trim().toUpperCase();

/**
 * The search a form would run, in the exact shape the results page sends —
 * or null while the form doesn't hold a complete, valid search yet.
 *
 * `canonicalPassengers` sends the passengers through the results URL and back
 * (src/utils/passengerTypes.js), exactly as a search from the home page
 * reaches the results page, so the early search matches the one it asks for.
 */
export function buildFlightSearchParams({ origin, destination, from, to, passengers, cabinClass, canonicalPassengers = false, now = new Date() }) {
  const o = airportCode(origin);
  const d = airportCode(destination);
  if (!IATA.test(o) || !IATA.test(d) || o === d) return null;
  if (!validDate(from)) return null;
  const earliest = new Date(now);
  earliest.setHours(0, 0, 0, 0);
  earliest.setDate(earliest.getDate() + MIN_LEAD_DAYS);
  const depart = new Date(from);
  depart.setHours(0, 0, 0, 0);
  if (depart < earliest) return null;
  const slices = [{ origin: o, destination: d, departure_date: ymd(from) }];
  if (to !== undefined && to !== null) {
    if (!validDate(to)) return null;
    const ret = new Date(to);
    ret.setHours(0, 0, 0, 0);
    if (ret < depart) return null;
    slices.push({ origin: d, destination: o, departure_date: ymd(to) });
  }
  let pax = Array.isArray(passengers) ? passengers.filter((p) => p && p.type) : [];
  if (canonicalPassengers) pax = readPassengerParams(writePassengerParams(new URLSearchParams(), pax));
  if (pax.length === 0) return null;
  return { slices, passengers: pax, cabin_class: cabinClass || 'economy' };
}

/** What makes two searches the same search (search_method doesn't change the results). */
export function flightSearchKey(params) {
  if (!params) return '';
  return JSON.stringify({
    slices: (params.slices || []).map((s) => [s.origin, s.destination, s.departure_date]),
    passengers: (params.passengers || []).map((p) => p.type),
    cabin: params.cabin_class || 'economy',
    excludeBasic: params.exclude_basic === true,
  });
}

let pending = null;

const unusable = (settled) => Boolean(
  settled && (settled.aborted || settled.status !== 200 || settled.meta?.partial),
);

/** Start (or keep) the early search for `params`; drops any other one. */
export function prefetchFlightSearch(params, { now = Date.now } = {}) {
  const key = flightSearchKey(params);
  if (!key) return;
  // On an older build, pressing Search loads the new one
  // (src/lib/searchNavigation.js), which would throw this search away.
  if (newerBuildIsLive()) return;
  if (pending && pending.key === key && now() - pending.startedAt < PREFETCH_MAX_AGE_MS && !unusable(pending.settled)) return;
  cancelPrefetchedFlightSearch();
  const controller = new AbortController();
  const entry = { key, startedAt: now(), controller, latest: null, listener: null, settled: null };
  entry.result = streamGoogleFlights(params, {
    signal: controller.signal,
    onFlights: (flights, info) => {
      entry.latest = { flights, info };
      if (entry.listener) entry.listener(flights, info);
    },
  }).then(
    (result) => { entry.settled = result; return result; },
    (error) => { entry.settled = { aborted: true, error }; return entry.settled; },
  );
  pending = entry;
}

/** Drop the early search, if any, and stop its request. */
export function cancelPrefetchedFlightSearch() {
  if (pending) pending.controller.abort();
  pending = null;
}

/**
 * Hand the early search to the page that pressed Search — or null, after
 * dropping it, when it isn't this search or can't be trusted any more.
 * The handle replays what already arrived to `attach(onFlights)`, forwards
 * the rest, and `result` settles like streamGoogleFlights does (an abort
 * rejects with an AbortError).
 */
export function takePrefetchedFlightSearch(params, { now = Date.now } = {}) {
  const entry = pending;
  pending = null;
  if (!entry) return null;
  const sameSearch = entry.key === flightSearchKey(params);
  const fresh = now() - entry.startedAt < PREFETCH_MAX_AGE_MS;
  if (!sameSearch || !fresh || unusable(entry.settled)) {
    entry.controller.abort();
    return null;
  }
  return {
    startedAt: entry.startedAt,
    attach(onFlights) {
      entry.listener = onFlights;
      if (entry.latest) onFlights(entry.latest.flights, entry.latest.info);
    },
    result: entry.result.then((result) => {
      if (result && result.aborted) {
        throw (result.error && result.error.name === 'AbortError') ? result.error : new DOMException('Aborted', 'AbortError');
      }
      return result;
    }),
    abort: () => entry.controller.abort(),
  };
}
